import { cn } from '@/utils';
import { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';

interface MobileCardProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  /**
   * accessorKey / id values always visible on the card face.
   * Tip for Member table: ['userName', 'balance', 'exposure', 'totalpl', 'status']
   * Everything else (except actionKeys) goes into the expandable drawer.
   */
  pinnedKeys?: string[];
  /**
   * Column ids rendered as quick-action buttons in the action row.
   * Default: ['actions']
   */
  actionKeys?: string[];
  isLoading?: boolean;
  rows?: number;
  renderSubRow?: (rowData: TData) => React.ReactNode;
  enableAccordion?: boolean;
  rowClassNames?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Renders a column's cell without a real table instance. */
function renderCell<TData>(col: ColumnDef<TData>, row: TData, rowIndex: number): React.ReactNode {
  const fakeRow = {
    original: row,
    index: rowIndex,
    getValue: (key: string) => (row as Record<string, unknown>)[key],
    getIsSelected: () => false,
    getIsExpanded: () => false,
    getCanExpand: () => false,
    getToggleExpandedHandler: () => () => {},
    toggleSelected: () => {},
    id: String(rowIndex),
    depth: 0,
    subRows: [],
    getAllCells: () => [],
    getVisibleCells: () => [],
  } as unknown as Row<TData>;

  const ctx = {
    row: fakeRow,
    getValue: () => {
      const key = (col as { accessorKey?: string }).accessorKey;
      return key ? (row as Record<string, unknown>)[key] : undefined;
    },
    renderValue: () => null,
    table: {} as never,
    cell: {} as never,
    column: {
      id:
        (col as { id?: string; accessorKey?: string }).id ??
        (col as { accessorKey?: string }).accessorKey,
      columnDef: col,
      getCanSort: () => false,
      getIsSorted: () => false,
      getToggleSortingHandler: () => undefined,
    } as never,
  } as CellContext<TData, unknown>;

  if (typeof col.cell === 'function') return col.cell(ctx);

  const key = (col as { accessorKey?: string }).accessorKey as keyof TData | undefined;
  return key ? String((row as TData)[key] ?? '') : null;
}

function colKey<TData>(col: ColumnDef<TData>): string {
  return (col as { accessorKey?: string }).accessorKey ?? (col as { id?: string }).id ?? '';
}

function colHeader<TData>(col: ColumnDef<TData>): string {
  return typeof col.header === 'string' ? col.header : colKey(col);
}
function CardSkeleton({ count = 5 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="border border-green3 rounded-xl p-3 space-y-3 animate-pulse bg-muted"
        >
          <div className="flex justify-between items-center">
            <div className="h-4 w-32 bg-green3 rounded" />
            <div className="h-5 w-16 bg-green3 rounded-full" />
          </div>
          <div className="flex gap-4">
            <div className="h-8 flex-1 bg-green3 rounded" />
            <div className="h-8 flex-1 bg-green3 rounded" />
            <div className="h-8 flex-1 bg-green3 rounded" />
          </div>
          <div className="flex gap-2 pt-1 border-t border-green3">
            <div className="h-6 w-8 bg-green3 rounded" />
            <div className="h-6 w-8 bg-green3 rounded" />
            <div className="h-6 w-6 bg-green3 rounded" />
            <div className="h-6 w-6 bg-green3 rounded" />
          </div>
        </div>
      ))}
    </>
  );
}

// ─── Single Card ──────────────────────────────────────────────────────────────

function MemberCard<TData>({
  row,
  rowIndex,
  pinnedCols,
  drawerCols,
  actionCols,
  snoCol,
  renderSubRow,
  enableAccordion,
  rowClassNames,
}: {
  row: TData;
  rowIndex: number;
  pinnedCols: ColumnDef<TData>[];
  drawerCols: ColumnDef<TData>[];
  actionCols: ColumnDef<TData>[];
  snoCol?: ColumnDef<TData>;
  renderSubRow?: (rowData: TData) => React.ReactNode;
  enableAccordion?: boolean;
  rowClassNames?: string;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subRowOpen, setSubRowOpen] = useState(false);

  // First pinned col = "title" (username, name, id…)
  const titleCol = pinnedCols[0];
  // Status col if present in pinned
  const statusCol = pinnedCols.find((c) => colKey(c) === 'status');
  // Metric cols = everything else pinned (not title, not status, not sno)
  const metricCols = pinnedCols.filter(
    (c) => c !== titleCol && colKey(c) !== 'status' && colKey(c) !== 'sno'
  );
  const isContent = drawerCols.length > 0 || actionCols.length > 0 || enableAccordion;
  return (
    <div className={cn('border border-green3 rounded-xl overflow-hidden bg-muted', rowClassNames)}>
      {/* ── Card face ── */}
      <div className="p-2">
        {/* Top row: username + status */}
        <div className="flex items-center justify-between gap-2 max-md: mb-1">
          <div className="flex gap-1 min-w-0 items-baseline">
            {snoCol && (
              <div className="text-[10px] flex text-gray-400 mt-0.5">
                #{renderCell(snoCol, row, rowIndex)}
              </div>
            )}
            {titleCol && (
              <div className="text-[13px] font-medium truncate">
                {renderCell(titleCol, row, rowIndex)}
              </div>
            )}
          </div>
          {statusCol && <div className="flex-shrink-0">{renderCell(statusCol, row, rowIndex)}</div>}
        </div>

        {/* Metric row */}
        {metricCols.length > 0 && (
          <div className="flex gap-0 mb-2">
            {metricCols.map((col, i) => (
              <div
                key={colKey(col)}
                className={cn('flex-1', i !== 0 && 'border-l border-green3 pl-2.5')}
              >
                <div className="text-[14px] text-gray-800 mb-0.5 leading-tight">
                  {colHeader(col)}
                </div>
                <div className="text-[12px] font-medium leading-tight">
                  {renderCell(col, row, rowIndex)}
                </div>
              </div>
            ))}
          </div>
        )}
        {isContent && (
          <div
            className={cn('flex items-center gap-2 pt-1.5  flex-wrap', 'border-t border-green3')}
          >
            {actionCols.map((col) => (
              <div key={colKey(col)} className="flex items-center gap-2">
                {renderCell(col, row, rowIndex)}
              </div>
            ))}

            <div className="flex-1" />

            {/* Sub-row toggle (accordion) */}
            {enableAccordion && renderSubRow && (
              <button
                onClick={() => setSubRowOpen((v) => !v)}
                className="text-[11px] text-gray-400 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
                aria-expanded={subRowOpen}
              >
                <span>Details</span>
                <ChevronDown
                  size={13}
                  className={cn('transition-transform duration-200', subRowOpen && 'rotate-180')}
                />
              </button>
            )}

            {/* Drawer toggle */}
            {drawerCols.length > 0 && (
              <button
                onClick={() => setDrawerOpen((v) => !v)}
                className="text-[11px] text-gray-400 flex items-center gap-1 cursor-pointer ml-1 bg-transparent border-none p-0"
                aria-expanded={drawerOpen}
              >
                <span>{drawerOpen ? 'Less' : `${drawerCols.length} more`}</span>
                <ChevronDown
                  size={13}
                  className={cn('transition-transform duration-200', drawerOpen && 'rotate-180')}
                />
              </button>
            )}
          </div>
        )}
        {/* Action row */}
      </div>

      {/* ── Accordion sub-row ── */}
      {enableAccordion && renderSubRow && subRowOpen && (
        <div className="border-t border-green3 p-2 bg-muted/50">{renderSubRow(row)}</div>
      )}

      {/* ── Expandable drawer ── */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          drawerOpen ? 'max-h-[600px]' : 'max-h-0'
        )}
        aria-hidden={!drawerOpen}
      >
        <div className="border-t border-green3  p-3">
          <div className="grid grid-col-1 gap-x-4 gap-y-1.5">
            {drawerCols.map((col) => (
              <div key={colKey(col)} className="min-w-0  flex justify-between">
                <div className="text-[10px] text-gray-400 mb-0.5 truncate min-w-16">
                  {colHeader(col)}
                </div>
                <div className="text-[10px] font-medium leading-tight">
                  {renderCell(col, row, rowIndex)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MobileCard<TData>({
  data,
  columns,
  pinnedKeys,
  actionKeys = ['actions'],
  isLoading,
  rows = 5,
  renderSubRow,
  enableAccordion = false,
  rowClassNames,
}: MobileCardProps<TData>) {
  const { pinned, drawer, actions, snoCol } = useMemo(() => {
    const actionSet = new Set(actionKeys);

    // Default pinned: first 3 non-action, non-utility columns
    const defaultPinned = columns
      .filter((c) => {
        const k = colKey(c);
        return !actionSet.has(k) && k !== 'expander' && k !== 'select';
      })
      .slice(0, 3)
      .map(colKey);

    const pinnedSet = new Set(pinnedKeys ?? defaultPinned);

    const pinned: ColumnDef<TData>[] = [];
    const drawer: ColumnDef<TData>[] = [];
    const actions: ColumnDef<TData>[] = [];
    let snoCol: ColumnDef<TData> | undefined;
    for (const col of columns) {
      const k = colKey(col);
      if (k === 'expander' || k === 'select') continue;
      if (k === 'sno') {
        snoCol = col;
        continue;
      }
      if (actionSet.has(k)) actions.push(col);
      else if (pinnedSet.has(k)) pinned.push(col);
      else drawer.push(col);
    }

    return { pinned, drawer, actions, snoCol };
  }, [columns, pinnedKeys, actionKeys]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        <CardSkeleton count={rows} />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-green3 rounded-xl bg-muted">
        <img src="/images/empty.png" className="h-36 mb-3" alt="" />
        <p className="text-gray font-semibold text-lg">No data available.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {data.map((row, i) => (
        <MemberCard
          key={i}
          row={row}
          rowIndex={i}
          pinnedCols={pinned}
          drawerCols={drawer}
          actionCols={actions}
          snoCol={snoCol}
          renderSubRow={renderSubRow}
          enableAccordion={enableAccordion}
          rowClassNames={rowClassNames}
        />
      ))}
    </div>
  );
}

export default MobileCard;
